import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, MapPin } from 'lucide-react';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { ClaimInfoHeader, FlowBadges, type FlowRow } from './AcceptedFlowShared';
import { WelcomeCallBox } from './WelcomeCallBox';
import { IntakeSubmissionBox } from './IntakeSubmissionBox';
import { ClaimChat } from './ClaimChat';
import { ClaimEmailBox } from './ClaimEmailBox';

/**
 * Sección Welcome Call (Scheduling): pestañas To-Do / Giving Problems, filas con
 * badges (WC / Intake / Photos / Contract), y panel desplegado con info del claim +
 * 2 columnas (llamada · formulario del intake) + updates + emails.
 */
function WelcomeCallRow({ r, onChanged }: { r: FlowRow; onChanged: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:wc:${r.claim_id}`, false);
  return (
    <div className="card" style={{ padding: 12 }}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)} style={{ cursor: 'pointer' }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
          <b>{r.homeowner_name || r.claim_number || `#${r.claim_id}`}</b>
          {r.claim_number && <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', textDecoration: 'none', fontFamily: 'var(--font-mono)' }}>{r.claim_number}</Link>}
          {r.is_existing_claim && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--info-soft)', color: 'var(--info)', fontWeight: 'var(--fw-bold)' }}>{t('ird.existing')}</span>}
          <span style={{ flex: 1 }} />
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.assigned_person || r.assigned_team_name || ''}</span>
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{r.contractor_name || '—'}{r.address ? <> · <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</> : ''}</div>
        <div style={{ marginTop: 8 }}><FlowBadges r={r} /></div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--line)', marginTop: 10, paddingTop: 10, display: 'grid', gap: 12 }}>
          <ClaimInfoHeader claimId={r.claim_id} />
          {/* 2 columnas: izquierda la llamada, derecha el formulario del intake (prellenado). */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }}>
            <div><WelcomeCallBox claimId={r.claim_id} homeownerName={r.homeowner_name} onChanged={onChanged} /></div>
            <div><IntakeSubmissionBox claimId={r.claim_id} /></div>
          </div>
          <ClaimChat claimId={r.claim_id} channel="scheduling" title="Scheduling Department Updates" />
          <ClaimEmailBox claimId={r.claim_id} departmentKey="scheduling" />
          <Link to={`/claims/${r.claim_id}`} className="btn ghost sm" style={{ justifySelf: 'start' }}>{t('cdw.open')} →</Link>
        </div>
      )}
    </div>
  );
}

export function WelcomeCallSection({ rows, onChanged }: { rows: FlowRow[]; onChanged: () => void }) {
  const { t } = useI18n();
  const [tab, setTab] = useState<'todo' | 'problems'>('todo');
  const todo = rows.filter((r) => !r.wc_problem);
  const problems = rows.filter((r) => r.wc_problem);
  const shown = tab === 'todo' ? todo : problems;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden', alignSelf: 'start' }}>
        <button className={`btn ghost sm${tab === 'todo' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setTab('todo')}>{t('wcsec.todo')} {todo.length}</button>
        <button className={`btn ghost sm${tab === 'problems' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setTab('problems')}><AlertTriangle size={14} strokeWidth={1.75} /> {t('wcsec.problems')} {problems.length}</button>
      </div>
      {shown.length === 0 ? <div className="page-empty">{t('wcsec.empty')}</div>
        : <div style={{ display: 'grid', gap: 10 }}>{shown.map((r) => <WelcomeCallRow key={r.presence_id} r={r} onChanged={onChanged} />)}</div>}
    </div>
  );
}
